import { CAMERA_PRESENTER } from '@studio/constants/Sources';
import { RESET_USER_VIRTUAL_BACKGROUND } from '@studio/constants/VirtualBackground';
import { PASSED } from '@studio/constants/ReadinessCheck';
import { VENDOR_AGORA } from '@studio/constants/Vendor';
import { GenericCameraSourceSettings } from '@shoflo/polymer/components/SourceSettings/GenericCameraSourceSettings/GenericCameraSourceSettings';
import { AgoraCameraPreview } from '@shoflo/polymer/components/Join/AgoraCameraPreview/AgoraCameraPreview';
import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { GENERIC_AUDIO_VIDEO_SETTINGS_FORM } from 'constants/ModalTypes';
import { ModalBody } from 'reactstrap';
import setUserBackground from '@shoflo/studio-redux/actions/backgroundFilters/setUserBackground';
import setVideoInputDevice from '@shoflo/studio-redux/actions/meeting/setVideoInputDevice';
import setTransformedVideoInputs from '@shoflo/studio-redux/actions/meeting/setTransformedVideoInputs';
import addToAttendeeTiles from '@shoflo/studio-redux/actions/meeting/addToAttendeeTiles';
import setGenericAudioInputDevice from '@shoflo/studio-redux/actions/meeting/setGenericAudioInputDevice';
import clearModal from '@shoflo/studio-redux/actions/modal/clearModal/clearModal';
import showNotification from '@shoflo/studio-redux/actions/notification/showNotification';
import uploadUserVirtualBackground from '@shoflo/studio-redux/actions/user/uploadUserVirtualBackground';
import saveUserVirtualBackground from '@shoflo/studio-redux/actions/user/saveUserVirtualBackground';
import updatePresenterSettings from 'actions/Broadcast/presenters/updatePresenterSettings';
import { StyledModal } from './SourceSettings.styled';
import {
  clearVirtualBackground as agoraClearVirtualBackground,
  getVideoInput as agoraGetVideoInput,
  initializeVirtualBackground as agoraInitializeVirtualBackground
} from '@cvent/studio-agora/virtualBackgrounds/virtualBackground';

import {
  changeVideoInputDevice,
  changeUserBackground,
  toggleMirrorVideo
} from '@cvent/studio-agora/video/videoHelpers';
import { switchAudioInput as agoraSwitchAudioInput } from '@cvent/studio-agora/audio/audioHelper';
import { updateAgoraVideoDeviceListeners } from '@shoflo/studio-redux/utils/updateAgoraVideoDeviceListeners';

const VENDOR_CONFIG_MAP = {
  [VENDOR_AGORA]: {
    cameraPreviewElement: AgoraCameraPreview,
    clearVirtualBackground: agoraClearVirtualBackground,
    initializeBackgroundFilter: agoraInitializeVirtualBackground,
    getVideoInput: agoraGetVideoInput,
    switchAudioInput: agoraSwitchAudioInput
  }
};

const GenericSourceSettings = () => {
  const modal = useSelector(state => state.modal);
  const client = useSelector(state => state.meeting.client, shallowEqual);
  const attendeeTiles = useSelector(state => state.meeting.attendeeTiles);
  const attendeeId = useSelector(state => state.meeting.attendeeId);
  const videoInputDevice = useSelector(state => state.videoInputDevice);
  const audioInputDevice = useSelector(state => state.audioInputDevice);
  const audioOutputDevice = useSelector(state => state.audioOutputDevice);
  const userBackground = useSelector(state => state.userBackground);
  const transformedVideoInputs = useSelector(
    state => state.transformedVideoInputs
  );
  const user = useSelector(state => state.user, shallowEqual);
  const cameraStatus = useSelector(state => state.readinessCheck.camera);

  const dispatch = useDispatch();

  const currentVideoTrack = attendeeTiles[attendeeId];

  const isCameraAvailable = cameraStatus === PASSED;

  const isPresenterEditForm = modal?.type === GENERIC_AUDIO_VIDEO_SETTINGS_FORM;

  const vendor = modal?.initialValue?.vendor;

  if (!isPresenterEditForm || !modal?.initialValue || !vendor) return null;

  const vendorConfig = VENDOR_CONFIG_MAP[vendor];

  if (!vendorConfig) return null;

  const {
    cameraPreviewElement,
    clearVirtualBackground,
    initializeBackgroundFilter,
    getVideoInput,
    switchAudioInput
  } = vendorConfig;

  const closeModal = () => dispatch(clearModal(modal.type));

  const showThisModal =
    isPresenterEditForm && modal.initialValue?.source.type === CAMERA_PRESENTER;

  const onCancel = () => {
    closeModal();
  };

  const onSubmit = data => {
    const { audioInputDeviceId, title, description } = data;
    dispatch(setGenericAudioInputDevice(audioInputDeviceId));

    dispatch(
      updatePresenterSettings(modal.initialValue.source, {
        source_title: title,
        description
      })
    );
    closeModal();
  };

  const { title, description = '' } = modal.initialValue.source;

  const handleSaveUserVirtualBackground = (fileName, relativePath) =>
    dispatch(saveUserVirtualBackground(fileName, relativePath));
  const handleUploadUserVirtualBackground = file =>
    dispatch(uploadUserVirtualBackground(file));
  const handleResetUserVirtualBackground = payload =>
    dispatch({ type: RESET_USER_VIRTUAL_BACKGROUND, payload });

  const deviceActions = {
    addToAttendeeTiles,
    handleVideoInputChanged: setVideoInputDevice,
    handleAudioInputChanged: setGenericAudioInputDevice,
    showNotification
  };

  const updateVideoInput = videoInput => {
    videoInput.videoTrack.userBackground = videoInput.virtualBackground;
    dispatch(addToAttendeeTiles(attendeeId, videoInput.videoTrack));
    dispatch(setUserBackground(videoInput.videoTrack.userBackground));
    dispatch(setTransformedVideoInputs([videoInput]));
    dispatch(setVideoInputDevice(videoInput));
    updateAgoraVideoDeviceListeners(videoInput, dispatch, attendeeId, client);
  };

  return (
    <StyledModal isOpen={showThisModal} backdrop="static" keyboard="false">
      <ModalBody className="p-1">
        <GenericCameraSourceSettings
          title={title}
          description={description}
          onSubmit={onSubmit}
          onCancel={onCancel}
          audioInputDeviceId={audioInputDevice}
          audioOutputDeviceId={audioOutputDevice}
          videoInputDeviceId={videoInputDevice?.device ?? videoInputDevice}
          isCameraAvailable={isCameraAvailable}
          userBackground={userBackground}
          videoInput={videoInputDevice}
          transformedVideoInputs={transformedVideoInputs}
          uploadUserVirtualBackground={handleUploadUserVirtualBackground}
          resetUserVirtualBackground={handleResetUserVirtualBackground}
          saveUserVirtualBackground={handleSaveUserVirtualBackground}
          user={user}
          CameraPreviewElement={cameraPreviewElement}
          clearVirtualBackground={clearVirtualBackground}
          initializeBackgroundFilter={initializeBackgroundFilter}
          getVideoInput={getVideoInput}
          switchAudioInput={switchAudioInput}
          currentVideoTrack={currentVideoTrack}
          client={client}
          dispatch={dispatch}
          deviceActions={deviceActions}
          changeVideoInputDevice={changeVideoInputDevice}
          updateVideoInput={updateVideoInput}
          changeUserBackground={changeUserBackground}
          toggleMirrorVideo={toggleMirrorVideo}
        />
      </ModalBody>
    </StyledModal>
  );
};

export { GenericSourceSettings };
